import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, Trophy, Target, Calendar, 
  Award, BarChart3, Loader2, ArrowUp, ArrowDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PerformanceDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [subjectStats, setSubjectStats] = useState([]);
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchPerformanceData();
  }, []);

  const fetchPerformanceData = async () => {
    try {
      const token = localStorage.getItem('studentToken');
      
      // Fetch comprehensive dashboard data
      const dashboardRes = await fetch(`${API_URL}/api/performance/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dashboardData = await dashboardRes.json();
      
      // Map backend response to frontend state
      setStats({
        tests_taken: dashboardData.total_tests,
        average_score: dashboardData.average_score,
        best_score: dashboardData.highest_score,
        total_questions_attempted: dashboardData.total_tests * 100 // Approximate
      });

      setSubjectStats(dashboardData.subject_wise_performance || []);
      setRecentAttempts(dashboardData.recent_attempts || []);

      // Fetch leaderboard separately
      const leaderRes = await fetch(`${API_URL}/api/performance/leaderboard`);
      const leaderData = await leaderRes.json();
      setLeaderboard(leaderData);

      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-red-600" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white">
      <Header />

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center">
                <TrendingUp className="mr-3 text-red-600" size={36} />
                Performance Dashboard
              </h1>
              <p className="text-gray-600">Track your progress and improve your performance</p>
            </div>

            {/* Overall Stats */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card className="border-l-4 border-l-blue-600">
                <CardContent className="p-6 text-center">
                  <Trophy className="mx-auto text-red-600 mb-3" size={40} />
                  <p className="text-3xl font-bold text-gray-900">{stats?.tests_taken || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Tests Taken</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-green-600">
                <CardContent className="p-6 text-center">
                  <Target className="mx-auto text-green-600 mb-3" size={40} />
                  <p className="text-3xl font-bold text-gray-900">{stats?.average_score?.toFixed(1) || 0}%</p>
                  <p className="text-sm text-gray-600 mt-1">Average Score</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-purple-600">
                <CardContent className="p-6 text-center">
                  <Award className="mx-auto text-purple-600 mb-3" size={40} />
                  <p className="text-3xl font-bold text-gray-900">{stats?.best_score || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Best Score</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-orange-600">
                <CardContent className="p-6 text-center">
                  <BarChart3 className="mx-auto text-orange-600 mb-3" size={40} />
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_questions_attempted || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Questions Solved</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Subject-wise Performance */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BarChart3 className="mr-2 text-red-600" size={20} />
                      Subject-wise Performance
                    </CardTitle>
                    <CardDescription>Your performance across different subjects</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    {subjectStats.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="text-gray-500">Take more tests to see subject-wise analysis</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {subjectStats.map((subject, idx) => {
                          const percentage = subject.average_percentage;
                          return (
                            <div key={idx} className="p-4 border rounded-lg hover:bg-gray-50">
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold text-gray-900">{subject.subject}</h3>
                                <span className="text-2xl font-bold text-red-600">{percentage.toFixed(1)}%</span>
                              </div>
                              
                              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                                <div 
                                  className={`h-3 rounded-full ${
                                    percentage >= 75 ? 'bg-green-500' :
                                    percentage >= 50 ? 'bg-yellow-500' :
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              
                              <div className="flex justify-between text-xs text-gray-600">
                                <span>Tests Taken: {subject.tests_taken}</span>
                                <span>Average Score: {percentage.toFixed(1)}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Attempts */}
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Calendar className="mr-2 text-red-600" size={20} />
                      Recent Test Attempts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {recentAttempts.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">No test attempts yet</p>
                        <Link to="/student/tests">
                          <Button className="mt-4 bg-red-600 hover:bg-red-700">
                            Take Your First Test
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {recentAttempts.map((attempt, idx) => (
                          <div key={idx} className="p-4 border rounded-lg flex items-center justify-between hover:bg-gray-50">
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">{attempt.test_title}</h3>
                              <p className="text-xs text-gray-500 mt-1">
                                {attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'Recently'}
                              </p>
                            </div>
                            
                            <div className="text-center mx-4">
                              <p className="text-2xl font-bold text-red-600">{attempt.percentage.toFixed(1)}%</p>
                              <p className="text-xs text-gray-500">{attempt.score}/{attempt.total_marks}</p>
                            </div>
                            
                            <Link to={`/student/test/${attempt.attempt_id}/review`} state={{ attemptId: attempt.attempt_id }}>
                              <Button variant="outline" size="sm">
                                Review
                              </Button>
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Leaderboard Sidebar */}
              <div>
                <Card className="sticky top-4">
                  <CardHeader className="bg-gradient-to-r from-yellow-50 to-orange-50">
                    <CardTitle className="flex items-center text-lg">
                      <Trophy className="mr-2 text-yellow-600" size={20} />
                      🏆 Leaderboard
                    </CardTitle>
                    <CardDescription>Top performers this month</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4">
                    {leaderboard.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-6">
                        No rankings available yet
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {leaderboard.slice(0, 10).map((student, idx) => (
                          <div 
                            key={idx}
                            className={`p-3 rounded-lg flex items-center space-x-3 ${
                              idx === 0 ? 'bg-yellow-50 border-2 border-yellow-400' :
                              idx === 1 ? 'bg-gray-100 border-2 border-gray-400' :
                              idx === 2 ? 'bg-orange-50 border-2 border-orange-400' :
                              'bg-gray-50 border border-gray-200'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                              idx === 0 ? 'bg-yellow-400 text-white' :
                              idx === 1 ? 'bg-gray-400 text-white' :
                              idx === 2 ? 'bg-orange-400 text-white' :
                              'bg-red-600 text-white'
                            }`}>
                              {idx < 3 ? ['🥇', '🥈', '🥉'][idx] : idx + 1}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-gray-900 truncate">
                                {student.student_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {student.total_tests} tests
                              </p>
                            </div>
                            
                            <div className="text-right">
                              <p className="font-bold text-red-600">{student.average_score.toFixed(1)}%</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PerformanceDashboard;
